import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BdCompanyContextService,
  BdShareablesService,
} from '@bindoc-projects/bindoc-permissions-lib/data';
import {
  BdCompany,
  BdCompanyGroup,
} from '@bindoc-projects/resource-domain-lib';
import { TreeNode } from 'primeng/api';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { SkeletonModule } from 'primeng/skeleton';
import { Tooltip } from 'primeng/tooltip';
import {
  TreeModule,
  TreeNodeExpandEvent,
  TreeNodeSelectEvent,
} from 'primeng/tree';
import { EMPTY, Observable, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  expand,
  reduce,
  take,
} from 'rxjs/operators';

import { SidebarNavigation, ViewTab } from '../shareables-overview.models';

interface SidebarNodeData {
  kind: 'tab' | 'company' | 'group' | 'more' | 'skeleton';
  tab?: ViewTab;
  companyId?: string;
  groupId?: string;
  groupName?: string;
  /**
   * True once opening this group came back 403 — the user is not a member. The
   * node stays visible (every group of the company is listed on purpose) but
   * becomes non-selectable, so the dead end is only ever reached once.
   */
  forbidden?: boolean;
  /** Hover explanation, shown when the node is not selectable. */
  tooltip?: string;
}

const MORE_KEY = '__more-companies__';

/**
 * Left navigation for the shareables overview. The fixed tabs and the
 * company/group scopes are rendered as one lazy `p-tree`: companies load
 * page-by-page (first page + a "load more" row), a company's groups load when
 * its node is expanded, and typing filters companies server-side. Nothing
 * fetches the full company/group list up front.
 */
@Component({
  selector: 'bd-scope-sidebar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <nav
      class="bd-scope-sidebar w-72 max-w-[85vw] h-full flex flex-col"
      [style.max-height]="maxHeight()"
      [ngClass]="
        bordered() ? 'border-r border-[var(--p-content-border-color)]' : ''
      "
    >
      @if (showFilter()) {
        <div class="shrink-0 px-3 pt-2 pb-1">
          <p-iconfield iconPosition="left" class="w-full">
            <p-inputicon>
              <i class="pi pi-search"></i>
            </p-inputicon>
            <input
              pInputText
              type="text"
              fluid
              (input)="onSearch($event)"
              placeholder="Kunde suchen"
              aria-label="Kunde suchen"
            />
          </p-iconfield>
        </div>
      }

      <div class="flex-1 min-h-0 overflow-y-auto py-1">
        @if (loading()) {
          <div
            class="flex flex-col px-3 pt-1"
            role="status"
            aria-label="Kunden werden geladen"
          >
            @for (width of skeletonRows; track $index) {
              <div class="flex items-center gap-2 py-2">
                <p-skeleton shape="circle" size="1rem" />
                <p-skeleton height="0.85rem" [width]="width" />
              </div>
            }
          </div>
        } @else {
          <p-tree
            [value]="displayNodes()"
            selectionMode="single"
            [(selection)]="selection"
            (onNodeExpand)="onExpand($event)"
            (onNodeSelect)="onSelect($event)"
          >
            <ng-template #node let-node>
              @if (node.type === 'skeleton') {
                <div class="flex items-center gap-2 w-full">
                  <p-skeleton shape="circle" size="1rem" />
                  <span class="flex-1 min-w-0">
                    <p-skeleton height="0.85rem" />
                  </span>
                </div>
              } @else {
                <span
                  [pTooltip]="$safeNavigationMigration(node.data?.tooltip)"
                  tooltipPosition="top"
                  [tooltipDisabled]="!node.data?.tooltip"
                >
                  {{ node.label }}
                  @if (node.data?.forbidden) {
                    <i class="pi pi-lock text-muted-color ml-2 text-sm"></i>
                  }
                </span>
              }
            </ng-template>
          </p-tree>
        }
      </div>
    </nav>
  `,
  styles: [
    `
      :host {
        display: block;
        height: 100%;
      }
      :host ::ng-deep .p-tree {
        border: none;
        padding: 0;
      }
      /* Smaller, tighter rows so long company/group names fit on one line and
         wrap cleanly at word boundaries instead of breaking mid-word. */
      :host ::ng-deep .p-tree-node-content {
        padding-block: 0.25rem;
        font-size: 0.8125rem;
        line-height: 1.3;
      }
      :host ::ng-deep .p-tree-node-label {
        overflow-wrap: break-word;
        word-break: normal;
      }
      /* Let the load-more skeleton bar span the full row width. */
      :host ::ng-deep .bd-scope-sidebar__skeleton-node .p-tree-node-label {
        flex: 1 1 auto;
        min-width: 0;
      }
    `,
  ],
  imports: [
    NgClass,
    TreeModule,
    SkeletonModule,
    IconField,
    InputIcon,
    InputText,
    Tooltip,
  ],
})
export class BdScopeSidebarComponent implements OnInit {
  private static readonly COMPANY_PAGE_SIZE = 25;
  private static readonly GROUP_PAGE_SIZE = 100;
  /**
   * Styleguide `FILTER_THRESHOLD` (see `.claude/docs/styleguide/forms.md`): a
   * filter field is only worth showing once the list has more than three
   * options — below that the user can scan it at a glance.
   */
  private static readonly FILTER_THRESHOLD = 3;

  public readonly bordered = input(true);
  /**
   * `false` drops the "Papierkorb" node. Archived items are not a scope a
   * picker can pick from, so the resource picker hides that destination.
   */
  public readonly showRecycleBin = input(true);
  /**
   * `undefined` means no scope currently applies (e.g. a global search is
   * active) — the tree renders with nothing highlighted.
   */
  public readonly activeTab = input<ViewTab | undefined>('recent');
  public readonly activeGroupId = input<string | undefined>(undefined);

  /**
   * Ids of groups that already answered 403 — the user is not a member. Every
   * group of a company stays listed (that is intentional), but a known-forbidden
   * one is rendered locked and non-selectable so it can't be clicked again.
   *
   * Membership is learned from responses that already happened rather than
   * probed up front: resolving it eagerly would cost one request per group.
   */
  public readonly forbiddenGroupIds = input<ReadonlySet<string>>(new Set());

  /**
   * Optional height cap (e.g. '70vh') for constrained hosts like the responsive
   * nav popover. The popover is appended to <body>, so it can't be bounded by
   * an ancestor's height; with this set the internal results list scrolls
   * within the cap and the search field stays pinned above it.
   */
  public readonly maxHeight = input<string | null>(null);

  public readonly navigate = output<SidebarNavigation>();

  public readonly nodes = signal<TreeNode[]>([]);
  /** Initial + filter reload — swaps the list for the skeleton. */
  public readonly loading = signal(false);
  /** Pagination ("Weitere Kunden laden") — keeps the tree, shows its mask. */
  public readonly loadingMore = signal(false);
  public selection: TreeNode | null = null;

  /**
   * Total number of companies the sidebar can list in the current mode — the
   * unfiltered count of the global list, or `1` when a single company is
   * pinned via the global company selector. Drives {@link showFilter}.
   */
  private readonly _companyTotal = signal(0);

  /**
   * The company search field only appears once there is more than
   * {@link FILTER_THRESHOLD} companies to sift through. When the global company
   * selector pins one company there is nothing left to filter, so it hides.
   */
  public readonly showFilter = computed(
    () => this._companyTotal() > BdScopeSidebarComponent.FILTER_THRESHOLD,
  );

  private readonly _search$ = new Subject<string>();

  /** Varied bar widths so the initial-load skeleton reads as a natural list. */
  public readonly skeletonRows = [
    '65%',
    '80%',
    '55%',
    '78%',
    '85%',
    '60%',
    '72%',
    '50%',
  ];

  /** Number of skeleton placeholder rows shown while paging in more companies. */
  private static readonly MORE_SKELETON_ROWS = 3;

  /**
   * Tree value with, while paging, skeleton placeholder rows spliced in where
   * the next companies will land — after the loaded companies and before the
   * "Papierkorb" tab — instead of the "load more" row.
   */
  public readonly displayNodes = computed<TreeNode[]>(() => {
    const nodes = this.nodes();
    if (!this.loadingMore()) {
      return nodes;
    }
    const withoutMore = nodes.filter(
      (n) => (n.data as SidebarNodeData).kind !== 'more',
    );
    const skeletons = Array.from(
      { length: BdScopeSidebarComponent.MORE_SKELETON_ROWS },
      (_, index) => this._skeletonNode(index),
    );
    const recycleBinIndex = withoutMore.findIndex(
      (n) => (n.data as SidebarNodeData).tab === 'recycle-bin',
    );
    if (recycleBinIndex === -1) {
      return [...withoutMore, ...skeletons];
    }
    return [
      ...withoutMore.slice(0, recycleBinIndex),
      ...skeletons,
      ...withoutMore.slice(recycleBinIndex),
    ];
  });

  private readonly _resourcesService = inject(BdShareablesService);
  private readonly _companyContext = inject(BdCompanyContextService);
  private readonly _destroyRef = inject(DestroyRef);

  private _companyPage = 0;
  private _companiesLast = true;
  private _query = '';
  // Guards against re-fetching while a deep-linked group is being revealed
  // (and against retrying a group that couldn't be resolved). Reset whenever
  // the company list is (re)loaded so a rebuilt tree can reveal again.
  private _revealAttemptedFor: string | null = null;

  constructor() {
    // A group only becomes known-forbidden after its 403 came back, by which
    // time its node is already in the tree. Re-mark the built nodes so the lock
    // appears without rebuilding (and re-fetching) the company's group list.
    effect(() => {
      const forbidden = this.forbiddenGroupIds();
      untracked(() =>
        this.nodes.update((nodes) => this._applyForbidden(nodes, forbidden)),
      );
    });

    // Keep the highlighted node in sync with the active tab/group. When the
    // active group lives under a company that hasn't been loaded/expanded yet
    // (deep link, "Im Ordner öffnen"), reveal it so the tree can highlight it
    // instead of leaving the selection empty.
    effect(() => {
      const tab = this.activeTab();
      const groupId = this.activeGroupId();
      if (tab === undefined) {
        this.selection = null;
        return;
      }
      const match = this._findActiveNode(this.nodes(), tab, groupId);
      this.selection = match;
      if (tab === 'group' && groupId && !match) {
        this._revealGroup(groupId);
      }
    });

    // Debounce keystrokes so the server isn't hit (and the skeleton doesn't
    // flash) on every character.
    this._search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((query) => this.applyFilter(query));
  }

  ngOnInit(): void {
    // Follow the global company selector: when one company is picked the
    // sidebar scopes down to just that company (and its groups); with none
    // picked it lists every company the user can reach. `selectedCompany$` is a
    // BehaviorSubject, so this fires synchronously with the current selection
    // and drives the initial load too.
    this._companyContext.selectedCompany$
      .pipe(
        distinctUntilChanged((a, b) => a?.id === b?.id),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((company) => {
        this._applySelectedCompany(company);
      });
  }

  private _applySelectedCompany(company: BdCompany | undefined): void {
    // A mode switch always starts from a clean query — the search field is
    // hidden in single-company mode, so a stale filter must not linger.
    this._query = '';
    if (company) {
      this._loadSelectedCompany(company);
    } else {
      this._reloadCompanies('');
    }
  }

  /**
   * Pins the sidebar to a single, globally-selected company: shows only that
   * company node (no other companies, no "load more") and eagerly loads and
   * expands its groups. The filter hides because one company is nothing to
   * sift through.
   */
  private _loadSelectedCompany(company: BdCompany): void {
    this._companiesLast = true;
    this._companyTotal.set(1);
    const node = this._companyNode(company);
    this.nodes.set(this._buildModel([node]));
    this._revealAttemptedFor = null;
    this.loading.set(false);
    this._expandCompanyGroups(node);
  }

  public onSearch(event: Event): void {
    this._search$.next((event.target as HTMLInputElement).value);
  }

  public onExpand(event: TreeNodeExpandEvent): void {
    const node = event.node;
    if (
      !node ||
      (node.data as SidebarNodeData | undefined)?.kind !== 'company' ||
      (node.children?.length ?? 0) > 0
    ) {
      return;
    }
    const companyId = (node.data as SidebarNodeData).companyId;
    if (!companyId) {
      return;
    }
    node.loading = true;
    this._allGroupsOf(companyId).subscribe((groups) => {
      node.loading = false;
      this.nodes.update((nodes) =>
        this._replaceChildren(
          nodes,
          node.key as string,
          groups.map((g) => this._groupNode(g)),
        ),
      );
    });
  }

  public onSelect(event: TreeNodeSelectEvent): void {
    const data = event.node?.data as SidebarNodeData | undefined;
    if (!data) {
      return;
    }
    switch (data.kind) {
      case 'tab':
        if (data.tab) {
          this.navigate.emit({ tab: data.tab });
        }
        break;
      case 'group':
        // `selectable: false` already stops PrimeNG from selecting a locked
        // node; this also covers a programmatic selection (e.g. a deep link).
        if (data.forbidden) {
          this.selection = null;
          return;
        }
        this.navigate.emit({
          tab: 'group',
          groupId: data.groupId,
          groupName: data.groupName,
        });
        break;
      case 'more':
        // Action row, not a destination — reset selection and page in more.
        this.selection = null;
        this._loadMoreCompanies();
        break;
    }
  }

  /** Reloads companies server-side for the (trimmed) query. */
  public applyFilter(query: string): void {
    const trimmed = query.trim();
    if (trimmed === this._query) {
      return;
    }
    this._query = trimmed;
    this._reloadCompanies(trimmed);
  }

  private _reloadCompanies(query: string): void {
    this.loading.set(true);
    this._resourcesService
      .searchMyCompanies(0, BdScopeSidebarComponent.COMPANY_PAGE_SIZE, query)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (page) => {
          this._companyPage = page.number;
          this._companiesLast = page.last;
          // Base the filter's visibility on the *unfiltered* total so typing a
          // query that narrows the list to ≤ 3 can never hide the field the
          // user is typing in.
          if (query === '') {
            this._companyTotal.set(page.totalElements);
          }
          this.nodes.set(
            this._buildModel(page.content.map((c) => this._companyNode(c))),
          );
          // The tree was rebuilt from scratch — allow the active group to be
          // revealed against the fresh company list.
          this._revealAttemptedFor = null;
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  private _loadMoreCompanies(): void {
    // The mask no longer blocks the row, so guard against a double click paging
    // in the same page twice (which would duplicate companies).
    if (this._companiesLast || this.loadingMore()) {
      return;
    }
    this.loadingMore.set(true);
    this._resourcesService
      .searchMyCompanies(
        this._companyPage + 1,
        BdScopeSidebarComponent.COMPANY_PAGE_SIZE,
        this._query,
      )
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (page) => {
          this._companyPage = page.number;
          this._companiesLast = page.last;
          this.nodes.update((nodes) => {
            const companies = nodes.filter(
              (n) => (n.data as SidebarNodeData).kind === 'company',
            );
            return this._buildModel([
              ...companies,
              ...page.content.map((c) => this._companyNode(c)),
            ]);
          });
          // A newly paged-in company may be the one holding the active group.
          this._revealAttemptedFor = null;
          this.loadingMore.set(false);
        },
        error: () => this.loadingMore.set(false),
      });
  }

  /** Walks every page of a single company's groups (bounded, on demand). */
  private _allGroupsOf(companyId: string): Observable<BdCompanyGroup[]> {
    const size = BdScopeSidebarComponent.GROUP_PAGE_SIZE;
    return this._resourcesService.groupsOfCompany(companyId, 0, size).pipe(
      expand((page) =>
        page.last ||
        page.content.length === 0 ||
        page.number + 1 >= page.totalPages
          ? EMPTY
          : this._resourcesService.groupsOfCompany(
              companyId,
              page.number + 1,
              size,
            ),
      ),
      reduce(
        (all: BdCompanyGroup[], page) => all.concat(page.content),
        [] as BdCompanyGroup[],
      ),
    );
  }

  private _buildModel(companyNodes: TreeNode[]): TreeNode[] {
    const more = this._companiesLast ? [] : [this._moreNode()];
    // While searching, list only the matching companies — the fixed tabs
    // aren't scopes and would be noise in a filtered result.
    if (this._query !== '') {
      return [...companyNodes, ...more];
    }
    return [
      this._tabNode('recent', 'Kürzlich verwendet', 'pi pi-clock'),
      this._tabNode('shareables', 'Eigene Ressourcen', 'pi pi-user'),
      this._tabNode('grants', 'Geteilt', 'pi pi-share-alt'),
      ...companyNodes,
      ...more,
      ...(this.showRecycleBin()
        ? [this._tabNode('recycle-bin', 'Papierkorb', 'pi pi-trash')]
        : []),
    ];
  }

  private _tabNode(tab: ViewTab, label: string, icon: string): TreeNode {
    return {
      key: `tab:${tab}`,
      label,
      icon,
      leaf: true,
      data: { kind: 'tab', tab } satisfies SidebarNodeData,
    };
  }

  private _companyNode(company: BdCompany): TreeNode {
    return {
      key: `company:${company.id}`,
      label: company.name,
      icon: 'pi pi-building',
      selectable: false,
      leaf: false,
      children: [],
      data: {
        kind: 'company',
        companyId: company.id,
      } satisfies SidebarNodeData,
    };
  }

  /**
   * Re-marks already-built group nodes against the current forbidden set.
   * Returns the same array when nothing changes so the tree isn't re-rendered
   * on every emission.
   */
  private _applyForbidden(
    nodes: TreeNode[],
    forbidden: ReadonlySet<string>,
  ): TreeNode[] {
    let changed = false;
    const mapped = nodes.map((node) => {
      const children = node.children
        ? this._applyForbidden(node.children, forbidden)
        : node.children;
      const data = node.data as SidebarNodeData | undefined;
      const isForbidden =
        data?.kind === 'group' && !!data.groupId && forbidden.has(data.groupId);

      if (
        children === node.children &&
        (data?.forbidden ?? false) === isForbidden
      ) {
        return node;
      }
      changed = true;
      if (data?.kind !== 'group') {
        return { ...node, children };
      }
      return {
        ...node,
        children,
        selectable: !isForbidden,
        data: {
          ...data,
          forbidden: isForbidden,
          tooltip: isForbidden
            ? 'Kein Zugriff: Sie sind kein Mitglied dieser Gruppe.'
            : undefined,
        } satisfies SidebarNodeData,
      };
    });
    return changed ? mapped : nodes;
  }

  private _groupNode(group: BdCompanyGroup): TreeNode {
    const forbidden = this.forbiddenGroupIds().has(group.id);
    return {
      key: `group:${group.id}`,
      label: group.name,
      // The lock is appended after the name by the node template, so the
      // leading group icon stays and the row reads as "group, but locked".
      icon: 'pi pi-users',
      leaf: true,
      selectable: !forbidden,
      data: {
        kind: 'group',
        groupId: group.id,
        groupName: group.name,
        forbidden,
        tooltip: forbidden
          ? 'Kein Zugriff: Sie sind kein Mitglied dieser Gruppe.'
          : undefined,
      } satisfies SidebarNodeData,
    };
  }

  private _moreNode(): TreeNode {
    return {
      key: MORE_KEY,
      label: 'Weitere Kunden laden …',
      icon: 'pi pi-ellipsis-h',
      leaf: true,
      data: { kind: 'more' } satisfies SidebarNodeData,
    };
  }

  private _skeletonNode(index: number): TreeNode {
    return {
      key: `skeleton:${index}`,
      type: 'skeleton',
      label: '',
      leaf: true,
      selectable: false,
      styleClass: 'bd-scope-sidebar__skeleton-node',
      data: { kind: 'skeleton' } satisfies SidebarNodeData,
    };
  }

  private _findActiveNode(
    nodes: TreeNode[],
    tab: ViewTab | undefined,
    groupId: string | undefined,
  ): TreeNode | null {
    if (tab === undefined) {
      return null;
    }
    if (tab === 'group') {
      if (!groupId) {
        return null;
      }
      for (const node of nodes) {
        const match = node.children?.find(
          (c) => (c.data as SidebarNodeData).groupId === groupId,
        );
        if (match) {
          return match;
        }
      }
      return null;
    }
    return nodes.find((n) => (n.data as SidebarNodeData).tab === tab) ?? null;
  }

  /**
   * Brings a deep-linked group into view: resolves its company, makes sure that
   * company node exists (fetching it when it's outside the loaded page), then
   * expands it and loads its groups. The selection effect re-runs on each
   * `nodes()` change and highlights the group once it appears.
   */
  private _revealGroup(groupId: string): void {
    if (this._revealAttemptedFor === groupId) {
      return;
    }
    this._revealAttemptedFor = groupId;
    this._resourcesService
      .group(groupId)
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (group) => this._revealCompany(group.companyId),
        error: () => undefined,
      });
  }

  private _revealCompany(companyId: string): void {
    const existing = this.nodes().find((n) => {
      const data = n.data as SidebarNodeData;
      return data.kind === 'company' && data.companyId === companyId;
    });
    if (existing) {
      this._expandCompanyGroups(existing);
      return;
    }
    this._resourcesService
      .company(companyId)
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (company) => {
          const node = this._companyNode(company);
          this.nodes.update((nodes) => this._appendCompany(nodes, node));
          this._expandCompanyGroups(node);
        },
        error: () => undefined,
      });
  }

  private _expandCompanyGroups(companyNode: TreeNode): void {
    const companyId = (companyNode.data as SidebarNodeData).companyId;
    if (!companyId) {
      return;
    }
    if ((companyNode.children?.length ?? 0) > 0) {
      // Groups already loaded — just make sure the node is expanded so the
      // effect can see (and highlight) the group child.
      this.nodes.update((nodes) =>
        nodes.map((n) =>
          n.key === companyNode.key ? { ...n, expanded: true } : n,
        ),
      );
      return;
    }
    this._allGroupsOf(companyId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((groups) => {
        this.nodes.update((nodes) =>
          this._replaceChildren(
            nodes,
            companyNode.key as string,
            groups.map((g) => this._groupNode(g)),
          ),
        );
      });
  }

  /** Adds a revealed company to the tree (idempotent by company id). */
  private _appendCompany(nodes: TreeNode[], companyNode: TreeNode): TreeNode[] {
    const newId = (companyNode.data as SidebarNodeData).companyId;
    const companies = nodes.filter(
      (n) => (n.data as SidebarNodeData).kind === 'company',
    );
    if (
      companies.some((c) => (c.data as SidebarNodeData).companyId === newId)
    ) {
      return nodes;
    }
    return this._buildModel([...companies, companyNode]);
  }

  private _replaceChildren(
    nodes: TreeNode[],
    targetKey: string,
    children: TreeNode[],
  ): TreeNode[] {
    return nodes.map((node) =>
      node.key === targetKey ? { ...node, expanded: true, children } : node,
    );
  }
}
